import * as React from "react";
import { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import PostAddIcon from "@mui/icons-material/PostAdd";
import DownloadIcon from "@mui/icons-material/Download";
import { GridColDef } from "@mui/x-data-grid";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import { CSVLink } from "react-csv";

// コンポーネント
import { LectureAdd } from "../../components/admin/LectureAdd";
import { VideoAdd } from "../../components/admin/VideoAdd";
import { Alert } from "../../components/admin/Alert";
import { VideoPreviewDialog } from "../../components/admin/VideoPreviewDialog";
import { TableLayout } from "../../components/layout/TableLayout";

// カスタムフックとユーティリティ
import { useDialog, useVideoDialog } from "../../hooks/useDialog";
import { Lecture, useLectureData } from "../../hooks/useLectureData";
import { useCSVDownload } from "../../hooks/useCSVDownload";
import { Button } from "@mui/material";
import { DialogTable } from "../../components/layout/DialogTable";
import { colors } from "../../theme/colors";

export default function LectureList() {
  // ダイアログの状態管理
  const lectureAddDialog = useDialog();
  const videoAddDialog = useDialog();
  const videosDialog = useDialog();
  // 選択中の講座は id だけ保持し、表示は lectureData から都度導出する。
  // Lecture オブジェクトのスナップショットを保持すると、動画追加後に refresh() で
  // lectureData を更新してもモーダル内の一覧が古いまま(再度開くまで反映されない)になる。
  const [selectedLectureId, setSelectedLectureId] = useState<string | null>(null);

  // 講座データ関連の状態管理
  const {
    lectureData,
    refresh,
    alertState,
    closeAlert
  } = useLectureData();

  // 常に最新の lectureData から選択中の講座を導出する
  const lecture = lectureData.find((l) => l.id === selectedLectureId) ?? null;

  // CSVダウンロード関連
  const {
    csvHeaders,
    csvData,
    csvLink,
    handleDownloadCSV
  } = useCSVDownload();

  // ビデオダイアログ関連
  const videoDialog = useVideoDialog({
    onErrorCallback: (error) => {
      if (error.code === 2) {
        videoDialog.handleCloseVideoDialog();
      }
      console.log("Error has Occurred:" + new Date().toISOString());
      console.log("Error:" + error);
    }
  });

  // 初期データ読み込み
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <TableLayout
        header={{
          title: "講座一覧",
          buttons: [
            {
              icon: <DownloadIcon sx={{ mr: 1 }} />,
              label: "csvダウンロード",
              onClick: handleDownloadCSV,
            },
            {
              icon: <CreateNewFolderIcon sx={{ mr: 1 }} />,
              label: "講座追加",
              onClick: lectureAddDialog.openDialog,
            },
          ]
        }}
        table={{
          data: lectureData,
          columns: buildColumns(setSelectedLectureId, videosDialog),
          getRowKey: (lecture) => lecture.id,
          ariaLabel: "lecture list table"
        }}
      />
      <Alert
        open={alertState.open}
        severity={alertState.severity}
        message={alertState.message}
        onClose={closeAlert}
      />
      <LectureAdd
        open={lectureAddDialog.isOpen}
        onClose={lectureAddDialog.closeDialog}
        onSuccess={() => {
          refresh();
        }}
      />
      <VideoAdd
        open={videoAddDialog.isOpen}
        onClose={videoAddDialog.closeDialog}
        onSuccess={() => {
          refresh();
        }}
        lectureData={lectureData}
      />
      <VideosDialogTable 
        open={videosDialog.isOpen}
        onClose={videosDialog.closeDialog}
        videoAddDialog={videoAddDialog}
        lecture={lecture}
        videoDialog={videoDialog}
      />
      <VideoPreviewDialog
        open={videoDialog.isOpen}
        onClose={videoDialog.handleCloseVideoDialog}
        content={videoDialog.content}
        videoUrl={videoDialog.videoUrl}
        onError={videoDialog.handleError}
      />
      <CSVLink
        headers={csvHeaders}
        data={csvData}
        filename={`${
          new Date(Date.now() + 9 * 60 * 60 * 1000) // 2025-08-25.csvのような感じでファイル名がつく
            .toISOString()
            .slice(0, 10)
        }.csv`}
        target="_blank"
        ref={csvLink}
        style={{ display: 'none' }}
      />
    </>
  );
}

function buildColumns(setSelectedLectureId: React.Dispatch<React.SetStateAction<string | null>>, videosDialog: { isOpen: boolean; openDialog: () => void; closeDialog: () => void; }): GridColDef<Lecture>[] {
  return [
    {
      field: 'lectureName',
      headerName: '講座名',
      flex: 5,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.row.lectureName}
        </Typography>
      ),
    },
    {
      field: 'videosCount',
      headerName: '動画数',
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" align="center">
          {params.row.videos.length}
        </Typography>
      ),
    },
    {
      field: 'videos',
      headerName: '動画一覧',
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          onClick={() => {
            setSelectedLectureId(params.row.id);
            videosDialog.openDialog();
          } }
          sx={{
            borderRadius: "20px",
            backgroundColor: colors.accent,
            "&:hover": {
              backgroundColor: colors.accent,
            },
          }}
        >
          <TaskOutlinedIcon />
        </Button>
      ),
    }
  ];
}

// 動画一覧ダイアログテーブルコンポーネント
function VideosDialogTable({ 
  open, 
  onClose, 
  videoAddDialog, 
  lecture, 
  videoDialog 
}: { 
  open: boolean; 
  onClose: () => void; 
  videoAddDialog: { openDialog: () => void }; 
  lecture: Lecture | null;
  videoDialog: {
    openVideoDialog: (content: string, fileName: string) => void;
  }
}) {
  return (
    <DialogTable
      open={open}
      onClose={onClose}
      title="動画一覧"
      headerButtons={[
        {
          icon: <PostAddIcon sx={{ mr: 1 }} />,
          label: "動画追加",
          onClick: videoAddDialog.openDialog,
        },
      ]}
      table={{
        data: lecture ? lecture.videos : [],
        columns: [
          { field: "videoName", headerName: "動画名", flex: 2 },
          {
            field: "preview", headerName: "プレビュー", flex: 1, renderCell: (params) => (
              <Button
                variant="contained"
                onClick={() => {
                  videoDialog.openVideoDialog(params.row.videoName, params.row.fileName);
                }}
                sx={{
                  borderRadius: "20px",
                  backgroundColor: colors.accent,
                  "&:hover": {
                    backgroundColor: colors.accent,
                  },
                }}
              >
                プレビュー
              </Button>
            )
          },
        ],
        getRowKey: (row) => row.id,
      }}
    />
  );
}
